import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { FiCheck, FiDownload, FiExternalLink, FiFile, FiFileText, FiRefreshCw, FiSearch, FiUploadCloud, FiX } from "react-icons/fi";
import { Modal } from "./Modal";
import { LoadingSpinner } from "./LoadingSpinner";
import { client } from "../Clients";
import type { StorageObject } from "../API";
import { getStoragePublicBaseUrl } from "../Helper/Constant";
import { resolveStorageUrl as resolveStorageUrlHelper } from "../Helper/storage";
import { normalizeStorageKey } from "../Helper/storage";
import type { HrefTarget } from "../types/layout";
import TaxonomyMultiSelect from "./TaxonomyMultiSelect";
import type { IApplicationState } from "../Redux/Reducers";

type FilePickerInputProps = {
	value?: HrefTarget | null;
	onChange: (next?: HrefTarget | null) => void;
	label?: string;
	helper?: string;
	disabled?: boolean;
	folder?: string;
	className?: string;
};

type UploadFormState = {
	title: string;
	fileName: string;
	folder: string;
	taxonomyIds: string[];
	file: File | null;
};

const DEFAULT_FOLDER = "documents";
const LIST_LIMIT = 160;

const initialUploadState: UploadFormState = {
	title: "",
	fileName: "",
	folder: "",
	taxonomyIds: [],
	file: null
};

const isAbsoluteUrl = (value: string) => /^https?:\/\//i.test(value);

const buildPublicUrl = (value?: string | null): string | null => {
	if (!value) return null;
	const resolved = resolveStorageUrlHelper(value);
	return resolved ? resolved : null;
};

// Pick a preview shape based on extension. Inline-rendered formats (PDF, images)
// get an embedded viewer; everything else falls back to a file-type chip + the
// download button. The Office Online viewer renders xlsx/docx/pptx from a
// publicly reachable URL — handy for read-only checks in the admin.
const detectKind = (urlOrKey?: string | null) => {
	if (!urlOrKey) return "unknown" as const;
	const lower = urlOrKey.split("?")[0].toLowerCase();
	if (/\.(pdf)$/i.test(lower)) return "pdf" as const;
	if (/\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(lower)) return "image" as const;
	if (/\.(xls|xlsx|csv)$/i.test(lower)) return "spreadsheet" as const;
	if (/\.(doc|docx|ppt|pptx|rtf|txt)$/i.test(lower)) return "document" as const;
	return "other" as const;
};

const DOCUMENT_EXTENSIONS = new Set([".pdf", ".doc", ".docx", ".ppt", ".pptx", ".txt", ".rtf"]);
const isDocument = (item: StorageObject) => {
	const ct = (item.contentType ?? "").toLowerCase();
	if (ct === "application/pdf" || ct.includes("pdf") || ct.includes("word") || ct.includes("presentation") || ct.startsWith("text/")) {
		return true;
	}
	const key = (item.key ?? item.fileName ?? "").toLowerCase();
	const ext = key.includes(".") ? key.slice(key.lastIndexOf(".")) : "";
	return DOCUMENT_EXTENSIONS.has(ext);
};

const normalizeIds = (ids?: Array<string | null>) =>
	(ids ?? []).filter((id): id is string => Boolean(id && id.trim())).map((id) => id.trim()).sort();

const sameIdList = (a?: Array<string | null>, b?: Array<string | null>) => {
	const left = normalizeIds(a);
	const right = normalizeIds(b);
	if (left.length !== right.length) return false;
	return left.every((value, index) => value === right[index]);
};

const buildTaxonomyLabel = (id: string, map: Map<string, string>) => map.get(id) ?? id;

/** Document picker input with a modal browser for selecting, uploading, and tagging storage documents. */
export const FilePickerInput: React.FC<FilePickerInputProps> = ({
	value,
	onChange,
	label,
	helper,
	disabled = false,
	folder = DEFAULT_FOLDER,
	className = ""
}) => {
	const [open, setOpen] = useState(false);
	const [assets, setAssets] = useState<StorageObject[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(1);
	const [hasMore, setHasMore] = useState(false);
	const [pendingKey, setPendingKey] = useState<string>("");
	const [pendingAsset, setPendingAsset] = useState<StorageObject | null>(null);
	const [uploading, setUploading] = useState(false);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
	const [uploadForm, setUploadForm] = useState<UploadFormState>(initialUploadState);
	const [taxonomyFilterIds, setTaxonomyFilterIds] = useState<string[]>([]);
	const [taxonomyDraftIds, setTaxonomyDraftIds] = useState<string[]>([]);
	const [taxonomySaving, setTaxonomySaving] = useState(false);
	const [taxonomyMessage, setTaxonomyMessage] = useState<string | null>(null);
	const [taxonomyEditing, setTaxonomyEditing] = useState(false);

	const taxonomies = useSelector((state: IApplicationState) => state.taxonomies.items);
	const taxonomyMap = useMemo(() => {
		const map = new Map<string, string>();
		taxonomies.forEach((taxonomie) => {
			if (taxonomie.id) {
				map.set(taxonomie.id, taxonomie.title ?? taxonomie.id);
			}
		});
		return map;
	}, [taxonomies]);

	const externalUrl = (value && typeof value === "object" && value.external) ? String(value.external).trim() : "";
	const currentKey = useMemo(() => {
		if (!externalUrl) return "";
		if (isAbsoluteUrl(externalUrl)) {
			const base = getStoragePublicBaseUrl().trim().replace(/\/+$/, "");
			if (base && externalUrl.startsWith(base)) {
				return externalUrl.slice(base.length).replace(/^\/+/, "");
			}
		}
		return externalUrl;
	}, [externalUrl]);

	const normalizedValue = useMemo(() => normalizeStorageKey(currentKey), [currentKey]);

	const displayValue = useMemo(() => {
		if (!normalizedValue) return "No document selected";
		const name = normalizedValue.split("/").pop() ?? normalizedValue;
		return name;
	}, [normalizedValue]);

	const loadAssets = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const taxonomyFilter = taxonomyFilterIds.length ? taxonomyFilterIds.join(",") : undefined;
			const response = await client.storage.apiStorageGet(folder, LIST_LIMIT + 1, page, search.trim() || undefined, taxonomyFilter);
			const items = (response.data ?? []).filter(isDocument);
			setHasMore(items.length > LIST_LIMIT);
			setAssets(items.slice(0, LIST_LIMIT));
		} catch (err) {
			console.error(err);
			setError("Unable to load documents from storage.");
			setHasMore(false);
		} finally {
			setLoading(false);
		}
	}, [folder, page, search, taxonomyFilterIds]);

	useEffect(() => {
		if (!open) return;
		setPendingKey(normalizedValue);
		setPendingAsset(null);
		setPage(1);
		setUploadForm(initialUploadState);
		setUploadError(null);
		setUploadSuccess(null);
		setTaxonomyDraftIds([]);
		setTaxonomyMessage(null);
		setTaxonomyEditing(false);
	}, [open, normalizedValue]);

	useEffect(() => {
		if (!open) return;
		const timeout = setTimeout(() => loadAssets(), 250);
		return () => clearTimeout(timeout);
	}, [open, loadAssets]);

	useEffect(() => {
		if (!open) return;
		setPage(1);
	}, [open, search, taxonomyFilterIds, folder]);

	useEffect(() => {
		if (!pendingKey || pendingAsset) return;
		const normalized = normalizeStorageKey(pendingKey);
		if (!normalized || isAbsoluteUrl(normalized)) return;
		const match = assets.find((asset) => normalizeStorageKey(asset.key) === normalized) ?? null;
		if (match) setPendingAsset(match);
	}, [assets, pendingAsset, pendingKey]);

	useEffect(() => {
		if (!pendingAsset) {
			setTaxonomyDraftIds([]);
			setTaxonomyEditing(false);
			return;
		}
		setTaxonomyDraftIds(normalizeIds(pendingAsset.taxonomyIds));
		setTaxonomyEditing(false);
	}, [pendingAsset]);

	const handleSelectAsset = (asset: StorageObject) => {
		const key = normalizeStorageKey(asset.key);
		setPendingKey(key);
		setPendingAsset(asset);
	};

	const taxonomyDirty = useMemo(
		() => (pendingAsset ? !sameIdList(pendingAsset.taxonomyIds ?? [], taxonomyDraftIds) : false),
		[pendingAsset, taxonomyDraftIds]
	);

	const handleSaveTaxonomies = async () => {
		if (!pendingAsset?.id || !taxonomyDirty) return;
		setTaxonomySaving(true);
		setTaxonomyMessage(null);
		try {
			const response = await client.storage.apiStorageIdTaxonomiesPut(pendingAsset.id, {
				taxonomyIds: taxonomyDraftIds
			});
			const updated = response.data ?? { ...pendingAsset, taxonomyIds: taxonomyDraftIds };
			setPendingAsset(updated);
			setAssets((prev) => prev.map((asset) => (asset.id === updated.id ? updated : asset)));
			setTaxonomyMessage("Taxonomies updated.");
			setTaxonomyEditing(false);
		} catch (err) {
			console.error(err);
			setTaxonomyMessage("Unable to update taxonomies.");
		} finally {
			setTaxonomySaving(false);
		}
	};

	const handleUploadInput = (key: keyof UploadFormState, value: string | string[] | File | null) => {
		setUploadForm((prev) => ({
			...prev,
			[key]: value as never
		}));
	};

	const handleFileChange = (file: File | null) => {
		handleUploadInput("file", file);
		setUploadSuccess(null);
		setUploadError(null);
		if (!file) return;
		if (!uploadForm.fileName) {
			const base = file.name.substring(0, file.name.lastIndexOf(".")) || file.name;
			handleUploadInput("fileName", base);
		}
		if (!uploadForm.title) {
			handleUploadInput("title", file.name.replace(/\.[^/.]+$/, ""));
		}
	};

	const handleUpload = async () => {
		if (!uploadForm.file) return;
		setUploading(true);
		setUploadError(null);
		setUploadSuccess(null);
		try {
			const taxonomyFilter = uploadForm.taxonomyIds.length ? uploadForm.taxonomyIds.join(",") : undefined;
			const response = await client.storage.apiStorageUploadPublicationsPost(
				uploadForm.title.trim(),
				uploadForm.file,
				uploadForm.fileName.trim() || undefined,
				uploadForm.folder.trim() || undefined,
				taxonomyFilter
			);
			const created = response.data ?? null;
			setUploadSuccess("Document uploaded successfully.");
			setUploadForm(initialUploadState);
			if (created?.key) {
				const key = normalizeStorageKey(created.key);
				setPendingKey(key);
				setPendingAsset(created);
			}
			loadAssets();
		} catch (err: any) {
			console.error(err);
			const apiMessage = err?.response?.data?.message ?? err?.response?.data ?? null;
			setUploadError(apiMessage ? `Upload failed: ${apiMessage}` : "Upload failed. Please try again.");
		} finally {
			setUploading(false);
		}
	};

	const handleClear = () => {
		onChange(null);
		setPendingKey("");
		setPendingAsset(null);
		setOpen(false);
	};

	const handleConfirm = (overrideKey?: string) => {
		const key = (overrideKey ?? pendingKey).trim();
		if (!key) return;
		const url = buildPublicUrl(normalizeStorageKey(key));
		if (url) {
			onChange({ external: url });
		}
		setOpen(false);
	};

	const hasFile = Boolean(normalizedValue.trim());
	const hasPendingSelection = Boolean(pendingKey.trim());
	const canUpload = Boolean(uploadForm.title.trim()) && Boolean(uploadForm.file);

	// Resolve the publicly reachable URL for the current value — used by every
	// preview branch below (PDF iframe, <img>, download button, Office viewer).
	const publicUrl = useMemo(() => (hasFile ? buildPublicUrl(normalizedValue) : null), [hasFile, normalizedValue]);
	const fileKind = useMemo(() => detectKind(normalizedValue), [normalizedValue]);
	const officeViewerUrl = useMemo(() => {
		// Microsoft's Office Online viewer renders any publicly fetchable xlsx /
		// docx / pptx as a read-only preview. The source URL has to be absolute
		// AND http(s) reachable from the public internet — falls back to "no
		// preview" if our storage isn't public.
		if (!publicUrl) return null;
		if (fileKind !== "spreadsheet" && fileKind !== "document") return null;
		if (!isAbsoluteUrl(publicUrl)) return null;
		return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(publicUrl)}`;
	}, [publicUrl, fileKind]);

	return (
		<div className={`space-y-1 ${className}`}>
			{label && <label className="text-xs font-semibold text-slate-600">{label}</label>}
			<div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm">
				<FiFile className="text-slate-500 shrink-0" />
				<div className="flex-1 min-w-0 truncate">{displayValue}</div>
				{!disabled && (
					<>
						{hasFile && (
							<button
								type="button"
								onClick={handleClear}
								className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 transition hover:border-slate-300 hover:text-slate-700"
								aria-label="Clear document"
							>
								<FiX />
							</button>
						)}
						<button
							type="button"
							onClick={() => setOpen(true)}
							className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-800"
						>
							{hasFile ? "Change" : "Pick document"}
						</button>
					</>
				)}
			</div>
			{hasFile && publicUrl && (
				<div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
					{fileKind === "pdf" && (
						<iframe
							title={`PDF preview ${displayValue}`}
							src={publicUrl + "#view=Fit&zoom=75"}
							className="h-[80vh] min-h-[640px] w-full rounded border border-slate-200 bg-white"
						/>
					)}
					{fileKind === "image" && (
						<a href={publicUrl} target="_blank" rel="noreferrer" className="block">
							<img
								src={publicUrl}
								alt={displayValue}
								className="max-h-72 w-full rounded border border-slate-200 bg-white object-contain"
							/>
						</a>
					)}
					{fileKind === "spreadsheet" && (
						officeViewerUrl ? (
							<iframe
								title={`Spreadsheet preview ${displayValue}`}
								src={officeViewerUrl}
								className="h-72 w-full rounded border border-slate-200 bg-white"
							/>
						) : (
							<div className="flex items-center gap-2 rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
								<FiFileText className="text-slate-500" />
								Aperçu tableur indisponible (le fichier doit être public). Téléchargez pour ouvrir.
							</div>
						)
					)}
					{fileKind === "document" && (
						officeViewerUrl ? (
							<iframe
								title={`Document preview ${displayValue}`}
								src={officeViewerUrl}
								className="h-72 w-full rounded border border-slate-200 bg-white"
							/>
						) : (
							<div className="flex items-center gap-2 rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
								<FiFileText className="text-slate-500" />
								Aperçu document indisponible. Téléchargez pour ouvrir.
							</div>
						)
					)}
					{fileKind === "other" && (
						<div className="flex items-center gap-2 rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
							<FiFile className="text-slate-500" /> Aperçu non disponible pour ce type de fichier.
						</div>
					)}
					<div className="flex flex-wrap items-center gap-2">
						<a
							href={publicUrl}
							download
							target="_blank"
							rel="noreferrer"
							className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
						>
							<FiDownload className="h-3.5 w-3.5" /> Télécharger
						</a>
						<a
							href={publicUrl}
							target="_blank"
							rel="noreferrer"
							className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
						>
							<FiExternalLink className="h-3.5 w-3.5" /> Ouvrir dans un nouvel onglet
						</a>
						<span className="text-[11px] text-slate-500 truncate">{normalizedValue}</span>
					</div>
				</div>
			)}
			{helper && <p className="text-[11px] text-slate-500">{helper}</p>}

			<Modal show={open} onHide={() => setOpen(false)} size="lg" centered>
				<Modal.Header className="bg-slate-900 text-white" closeButton>
					<div className="flex items-center gap-2">
						<FiFile />
						<Modal.Title>Select a document</Modal.Title>
					</div>
				</Modal.Header>
				<Modal.Body className="bg-slate-50">
					<div className="space-y-4">
						<div className="flex flex-wrap items-center gap-2">
							<div className="relative w-full max-w-xl">
								<FiSearch className="absolute left-3 top-3 text-slate-400" />
								<input
									className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
									placeholder="Search by title or key"
									value={search}
									onChange={(e) => setSearch(e.target.value)}
								/>
							</div>
							<div className="w-full max-w-sm">
								<TaxonomyMultiSelect
									label="Filter taxonomies"
									options={taxonomies}
									value={taxonomyFilterIds}
									onChange={setTaxonomyFilterIds}
									placeholder="Type to filter taxonomies"
								/>
							</div>
							<button
								type="button"
								onClick={loadAssets}
								className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-100"
							>
								<FiRefreshCw /> Refresh
							</button>
						</div>

						<div className="relative min-h-[280px] rounded-2xl border border-slate-200 bg-white/70 p-4">
							{loading && (
								<div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
									<LoadingSpinner />
								</div>
							)}
							{error && (
								<div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
									{error}
								</div>
							)}
							{!error && assets.length === 0 && !loading && (
								<div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
									No documents found in this folder. Upload documents to the &quot;{folder}&quot; folder in Storage.
								</div>
							)}
							<div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
								{assets.map((asset) => {
									const key = normalizeStorageKey(asset.key);
									const isSelected = key && key === normalizeStorageKey(pendingKey);
									return (
										<button
											key={asset.id ?? asset.key ?? key}
											type="button"
											onClick={() => handleSelectAsset(asset)}
											onDoubleClick={() => {
												const directKey = normalizeStorageKey(asset.key);
												handleSelectAsset(asset);
												if (directKey) handleConfirm(directKey);
											}}
											className={`rounded-xl border p-3 text-left shadow-sm transition ${
												isSelected ? "border-slate-900 bg-white ring-2 ring-slate-900" : "border-slate-200 bg-white hover:border-slate-300"
											}`}
										>
											<div className="mb-2 flex h-12 w-full items-center justify-center rounded-lg bg-slate-100">
												<FiFile className="h-6 w-6 text-slate-500" />
											</div>
											<p className="truncate text-xs font-semibold text-slate-900">{asset.title ?? asset.fileName ?? key}</p>
											<p className="truncate text-[11px] text-slate-500">{key || asset.folder || "—"}</p>
										</button>
									);
								})}
							</div>
							<div className="mt-4 flex items-center justify-between text-xs text-slate-500">
								<button
									type="button"
									onClick={() => setPage((current) => Math.max(1, current - 1))}
									disabled={page === 1 || loading}
									className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
								>
									Previous
								</button>
								<span>Page {page}</span>
								<button
									type="button"
									onClick={() => setPage((current) => current + 1)}
									disabled={!hasMore || loading}
									className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
								>
									Next
								</button>
							</div>
						</div>

						{pendingKey && (
							<div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 break-all">
								Selected: {normalizeStorageKey(pendingKey)}
							</div>
						)}

						{pendingAsset && (
							<div className="rounded-2xl border border-slate-200 bg-white p-4">
								<div className="flex items-center justify-between">
									<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Taxonomies</p>
									<button
										type="button"
										onClick={() => {
											if (!taxonomyEditing) {
												setTaxonomyEditing(true);
												return;
											}
											handleSaveTaxonomies();
										}}
										disabled={(taxonomyEditing ? !taxonomyDirty : false) || taxonomySaving || !pendingAsset.id}
										className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
									>
										{taxonomySaving ? "Saving…" : taxonomyEditing ? "Save" : "Edit"}
									</button>
								</div>
								<div className="mt-2">
									{taxonomyEditing ? (
										<TaxonomyMultiSelect
											options={taxonomies}
											value={taxonomyDraftIds}
											onChange={setTaxonomyDraftIds}
											placeholder="Assign taxonomies"
											helper="Filter and select taxonomies for this document."
										/>
									) : (
										<div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
											{pendingAsset.taxonomyIds?.length
												? pendingAsset.taxonomyIds.map((id) => buildTaxonomyLabel(id, taxonomyMap)).join(", ")
												: "No taxonomies assigned."}
										</div>
									)}
								</div>
								{taxonomyMessage && (
									<p className="mt-2 text-xs text-slate-500">{taxonomyMessage}</p>
								)}
							</div>
						)}

						<div className="flex justify-end gap-2">
							<button
								type="button"
								onClick={() => setOpen(false)}
								className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={() => handleConfirm()}
								disabled={!hasPendingSelection}
								className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								Use this document
							</button>
						</div>
					</div>
				</Modal.Body>
			</Modal>
		</div>
	);
};

export default FilePickerInput;
